import React from "react";
import { DatePicker, Input, InputNumber, Select } from "antd";
import moment from "moment";

import {
  appendChartSlab,
  PeriodType,
  removeChartSlab,
  setSlabStart,
  TChartSlab,
  TInterestRateChart,
  validateChart,
} from "../../../../helpers/interestRateChart";
import FormSection from "./FormSection";
import Field from "./Field";
import { Bars, Info, Plus, Trash } from "./icons";
import { MONEY_INPUT } from "./money";
import styles from "./fundForm.module.scss";

const PERIOD_TYPES = [
  { value: PeriodType.Days, label: "Days" },
  { value: PeriodType.Weeks, label: "Weeks" },
  { value: PeriodType.Months, label: "Months" },
  { value: PeriodType.Years, label: "Years" },
];

/** Bar heights read as a ladder, so the smallest band is still visible. */
const BAR_MIN = 34;
const BAR_MAX = 78;

interface IRateChartSection {
  value?: TInterestRateChart;
  onChange: (chart: TInterestRateChart) => void;
  currencyCode?: string;
  disabled?: boolean;
}

export function emptyInterestRateChart(name: string): TInterestRateChart {
  return {
    name,
    fromDate: moment().format("YYYY-MM-DD"),
    toDate: null,
    description: "",
    isPrimaryGroupingByAmount: true,
    chartSlabs: appendChartSlab([], true),
  };
}

/**
 * The rate bands a savings deposit pays.
 *
 * Reads as a ladder rather than a form: each band's END is derived from where
 * the next one starts, shown greyed, and the last is open. That is what makes
 * a gap or an overlap impossible to write down — the backend's contiguity rule
 * is `prev.to + 1`, which is easy to get wrong by hand and reports itself a
 * long way from the cause.
 */
export default function RateChartSection({
  value,
  onChange,
  currencyCode,
  disabled = false,
}: IRateChartSection) {
  const chart = value ?? emptyInterestRateChart("");
  /**
   * Always by amount.
   *
   * The chart belongs to a savings deposit and nothing else — confirmed with
   * the API owner — and Flexisave and Habit Save have no term, so there is no
   * period to band on. The DTO's other axis is left set to amount rather than
   * offered as a choice that could only ever be wrong.
   */
  const byAmount = true;
  const errors = validateChart(chart);
  const slabs = chart.chartSlabs;

  const update = (patch: Partial<TInterestRateChart>) =>
    onChange({ ...chart, ...patch });

  const updateSlab = (index: number, patch: Partial<TChartSlab>) =>
    update({
      chartSlabs: slabs.map((slab, i) =>
        i === index ? { ...slab, ...patch } : slab,
      ),
    });

  const errorFor = (index: number, field: keyof TChartSlab) =>
    errors.find((e) => e.slabIndex === index && e.field === field)?.message;

  const headerErrors = errors.filter((e) => e.slabIndex === undefined);

  const startField = byAmount ? "fromAmount" : "fromPeriod";
  const endField = byAmount ? "toAmount" : "toPeriod";
  const symbol = byAmount ? "₦" : "";

  const rates = slabs.map((slab) => Number(slab.interestRate) || 0);
  const topRate = Math.max(...rates, 0);

  const money = (n?: number | null) =>
    n === null || n === undefined
      ? ""
      : `${symbol}${Number(n).toLocaleString()}`;

  return (
    <FormSection
      id="rate-chart"
      title="Interest rate chart"
      subtitle="What this fund pays, by deposit size. Required for a savings deposit."
      icon={<Bars color="#713fff" />}
      meta={
        errors.length === 0
          ? `${slabs.length} ${slabs.length === 1 ? "band" : "bands"} · covers ${money(
              slabs[0]?.[startField],
            )} upward`
          : undefined
      }
      action={
        errors.length === 0 ? (
          <span className={styles.badgeOk}>
            {slabs.length} {slabs.length === 1 ? "band" : "bands"} &middot;
            covers {money(slabs[0]?.[startField])} upward
          </span>
        ) : (
          <span className={styles.badgeMuted}>{errors.length} to resolve</span>
        )
      }
    >
      <div className={styles.gridThirds} style={{ marginBottom: 20 }}>
        <Field required label="Chart name">
          <Input
            size="large"
            maxLength={100}
            placeholder="e.g. NGN Savings Rate Chart"
            value={chart.name ?? ""}
            disabled={disabled}
            onChange={(e) => update({ name: e.target.value })}
          />
        </Field>

        <Field required label="Effective from">
          <DatePicker
            size="large"
            style={{ width: "100%" }}
            format="DD MMM YYYY"
            allowClear={false}
            disabled={disabled}
            value={chart.fromDate ? moment(chart.fromDate) : null}
            onChange={(date) =>
              update({ fromDate: date ? date.format("YYYY-MM-DD") : null })
            }
          />
        </Field>

        <Field label="Until" optional>
          <DatePicker
            size="large"
            style={{ width: "100%" }}
            format="DD MMM YYYY"
            placeholder="No end date"
            disabled={disabled}
            value={chart.toDate ? moment(chart.toDate) : null}
            onChange={(date) =>
              update({ toDate: date ? date.format("YYYY-MM-DD") : null })
            }
          />
        </Field>
      </div>

      <div
        className={`${styles.ladder} ${!byAmount ? styles.ladderRowWide : ""}`}
        style={{ marginTop: byAmount ? 0 : 20 }}
      >
        <div
          className={`${styles.ladderHead} ${
            !byAmount ? styles.ladderHeadWide : ""
          }`}
        >
          <div />
          <div className={styles.ladderCol}>From</div>
          <div className={styles.ladderCol}>
            Up to
            <Info size={13} color="#b9bfc7" />
          </div>
          {!byAmount ? <div className={styles.ladderCol}>Unit</div> : null}
          <div className={styles.ladderCol}>Rate</div>
          <div className={styles.ladderCol}>Label</div>
          <div />
        </div>

        {slabs.map((slab, index) => {
          const isLast = index === slabs.length - 1;
          const end = slab[endField];
          const startError = errorFor(index, startField);
          const rateError = errorFor(index, "interestRate");

          return (
            <div
              key={index}
              className={`${styles.ladderRow} ${
                !byAmount ? styles.ladderRowWide : ""
              } ${index % 2 ? styles.ladderRowAlt : ""}`}
            >
              <div className={styles.ladderIndex}>{index + 1}</div>

              <div className={styles.ladderControl}>
                <InputNumber
                  aria-label={`Band ${index + 1} from`}
                  style={{ width: "100%" }}
                  prefix={byAmount ? symbol : undefined}
                  {...(byAmount ? MONEY_INPUT : {})}
                  disabled={disabled}
                  value={slab[startField] ?? undefined}
                  status={startError ? "error" : undefined}
                  onChange={(next) =>
                    update({
                      chartSlabs: setSlabStart(
                        slabs,
                        index,
                        next === null || next === undefined
                          ? null
                          : Number(next),
                        byAmount,
                      ),
                    })
                  }
                />
                {startError ? (
                  <span className={styles.error}>{startError}</span>
                ) : null}
              </div>

              <div
                className={`${styles.derived} ${
                  isLast ? styles.derivedOpen : ""
                }`}
                aria-label={`Band ${index + 1} to`}
              >
                {isLast ? "and above" : money(end)}
              </div>

              {!byAmount ? (
                <div className={styles.ladderControl}>
                  <Select
                    aria-label={`Band ${index + 1} unit`}
                    style={{ width: "100%" }}
                    disabled={disabled}
                    value={slab.periodType ?? PeriodType.Days}
                    options={PERIOD_TYPES}
                    onChange={(next) =>
                      // One period type governs the whole chart.
                      update({
                        chartSlabs: slabs.map((s) => ({
                          ...s,
                          periodType: Number(next),
                        })),
                      })
                    }
                  />
                </div>
              ) : null}

              <div className={styles.ladderControl}>
                <InputNumber
                  aria-label={`Band ${index + 1} rate`}
                  style={{ width: "100%" }}
                  suffix="%"
                  min={0}
                  max={100}
                  step={0.05}
                  disabled={disabled}
                  value={slab.interestRate}
                  status={rateError ? "error" : undefined}
                  onChange={(next) =>
                    updateSlab(index, { interestRate: Number(next ?? 0) })
                  }
                />
                {rateError ? (
                  <span className={styles.error}>{rateError}</span>
                ) : null}
              </div>

              <div className={styles.ladderControl}>
                <Input
                  aria-label={`Band ${index + 1} label`}
                  maxLength={200}
                  placeholder="Optional"
                  disabled={disabled}
                  value={slab.description ?? ""}
                  onChange={(e) =>
                    updateSlab(index, { description: e.target.value })
                  }
                />
              </div>

              <button
                type="button"
                className={styles.rowRemove}
                aria-label={`Remove band ${index + 1}`}
                disabled={disabled || slabs.length === 1}
                onClick={() =>
                  update({
                    chartSlabs: removeChartSlab(slabs, index, byAmount),
                  })
                }
              >
                <Trash />
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.ladderFoot}>
        <button
          type="button"
          className={styles.addBand}
          disabled={disabled}
          onClick={() =>
            update({ chartSlabs: appendChartSlab(slabs, byAmount) })
          }
        >
          <Plus color="#713fff" />
          Add band
        </button>

        <div className={styles.ladderFootNote}>
          Each band ends just below where the next one starts, so the ladder can
          never leave a gap.
        </div>
      </div>

      {headerErrors.length ? (
        <div style={{ marginTop: 16 }}>
          {headerErrors.map((error) => (
            <div key={error.message} className={styles.error}>
              {error.message}
            </div>
          ))}
        </div>
      ) : null}

      {byAmount && slabs.length > 0 && topRate > 0 ? (
        <div className={styles.preview}>
          <div className={styles.previewTitle}>What a customer earns</div>
          <div className={styles.previewBars}>
            {slabs.map((slab, index) => {
              const rate = Number(slab.interestRate) || 0;
              const height =
                BAR_MIN + (BAR_MAX - BAR_MIN) * (topRate ? rate / topRate : 0);
              const isTop = rate === topRate;
              const shade = isTop
                ? "#713FFF"
                : index === 0
                  ? "#D9CCFF"
                  : "#B79CFF";

              return (
                <div key={index} className={styles.previewBar}>
                  <div
                    className={styles.previewFill}
                    style={{
                      height,
                      background: shade,
                      color: isTop ? "#FFFFFF" : "#3F137D",
                    }}
                  >
                    {rate}%
                  </div>
                  <div className={styles.previewCaption}>
                    {money(slab[startField])}
                    {index === slabs.length - 1
                      ? "+"
                      : ` – ${money(slab[endField])}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </FormSection>
  );
}
